import { getStandardCompactEmailSignature } from '@modules/email-signatures-helpers';
// utils
import produce from 'immer';
import { type FC, useCallback, useState } from 'react';

import { EmailSignatureContactDetailsFieldWidth } from '../../consts';
// types
import {
  type EmailSignatureContactDetailsField,
  EmailSignatureContactDetailsFieldTypes,
  type EmailSignatureSocialLink,
  EmailSignatureSocialLinkTypes,
  type EmailSignatureV2,
} from '../../types';
// components
import EmailSignaturePreviewBox from '../EmailSignaturePreviewBox';
import EmailSignatureCompactLayout from '../layouts/compact/EmailSignatureCompactLayout';
import CompactPreviewShimmer from '../layouts/compact/EmailSignatureCompactLayoutShimmer';
// hooks
import { useDummyContactDetailsIconsGenerator } from '../useContactDetailsIconsGenerator';
import { useDummySocialLinksIconsGenerator } from '../useSocialLinksIconsGenerator';
import styles from './EmailSignatureEditorPreview.module.scss';

type EmailSignatureEditorPreviewProps = {
  isEverythingEmpty: boolean;
  emailSignature: EmailSignatureV2;
  isInTransitionState?: boolean;
  emailSignatureHasBeenLoaded: boolean;
};

/*
    This component will take care of displaying the email signature in preview mode, it will take care of generating and
    displaying dummy fields if the email signature is empty. It is normally used in conjuction with the SideEditorCoreServicesProvider
    , EmailSignatureMainEditor and EmailSignatureOnboardingEditor
*/
const EmailSignatureEditorPreview: FC<React.PropsWithChildren<EmailSignatureEditorPreviewProps>> = ({
  emailSignature,
  isInTransitionState = false,
  isEverythingEmpty,
  emailSignatureHasBeenLoaded,
}) => {
  const handleUpdateDummySocialLinkIconUrl = useCallback((index: number, imageUrl: string) => {
    setDummySocialLinks(
      produce((standardCompactSocialLinks) => {
        standardCompactSocialLinks[index].iconUrl = imageUrl;
      }),
    );
  }, []);

  const handleUpdateDummyContactIconUrl = useCallback((index: number, imageUrl: string) => {
    setDummyContactFields(
      produce((standardCompactContactFields) => {
        standardCompactContactFields[index].iconUrl = imageUrl;
      }),
    );
  }, []);

  const [standardCompactSocialLinks, setDummySocialLinks] = useState<EmailSignatureSocialLink[]>([
    {
      type: EmailSignatureSocialLinkTypes.LINKEDIN,
      iconUrl: '',
      id: 'sc_1',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.FACEBOOK,
      iconUrl: '',
      id: 'sc_2',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.TWITTER,
      iconUrl: '',
      id: 'sc_3',
      value: 'fakeValue',
    },
    {
      type: EmailSignatureSocialLinkTypes.YOUTUBE,
      iconUrl: '',
      id: 'sc_4',
      value: 'fakeValue',
    },
  ]);

  useDummySocialLinksIconsGenerator(
    emailSignature,
    handleUpdateDummySocialLinkIconUrl,
    standardCompactSocialLinks,
  );

  const [standardCompactContactFields, setDummyContactFields] = useState<EmailSignatureContactDetailsField[]>(
    [
      {
        baseIcon: 'envelope',
        iconUrl: '',
        value: 'johndoe@gmail.com',
        label: '',
        id: 'ct_1',
        type: EmailSignatureContactDetailsFieldTypes.EMAIL,
        fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
      },
      {
        baseIcon: 'phone-alt',
        iconUrl: '',
        value: '0491 570 006',
        label: '',
        id: 'ct_2',
        type: EmailSignatureContactDetailsFieldTypes.PHONE,
        fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
      },
      {
        baseIcon: 'map-marker-alt',
        iconUrl: '',
        value: '42 Wallaby Way, Sydney',
        label: '',
        id: 'ct_3',
        type: EmailSignatureContactDetailsFieldTypes.ADDRESS,
        fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
      },
      {
        baseIcon: 'browser',
        iconUrl: '',
        value: 'blinq.me',
        label: '',
        id: 'ct_4',
        type: EmailSignatureContactDetailsFieldTypes.WEBSITE,
        fieldWidth: EmailSignatureContactDetailsFieldWidth.HALF_WIDTH,
      },
    ],
  );

  useDummyContactDetailsIconsGenerator(
    emailSignature,
    handleUpdateDummyContactIconUrl,
    standardCompactContactFields,
  );

  return (
    <EmailSignaturePreviewBox>
      <div className={styles['preview-box-signature-body']}>
        {emailSignatureHasBeenLoaded && (
          <EmailSignatureCompactLayout
            disableAllSpecialEffects={isInTransitionState}
            disableAnchorLink={isInTransitionState}
            emailSignature={
              isEverythingEmpty
                ? getStandardCompactEmailSignature(
                    standardCompactContactFields,
                    standardCompactSocialLinks,
                    emailSignature,
                  )
                : emailSignature
            }
          />
        )}

        {!emailSignatureHasBeenLoaded && <CompactPreviewShimmer />}
      </div>
    </EmailSignaturePreviewBox>
  );
};

export default EmailSignatureEditorPreview;
